// CAR-118 — doctor pre-consult summary. A deterministic, PHI-free-of-raw-JSON
// reshaping of a medical-intake submission into a readable structure for the
// clinician dashboard: risk flags surfaced first, then the answers grouped by
// the template's sections with option values resolved to their labels. No
// clinical judgement is added — this only reformats the doctor's own data so
// the consult time goes to decisions, not reading raw JSON.
//
// Pure + locale-aware (sv/en); the API builds it from the latest intake
// submission + the published template, the admin renders it.

import { z } from "zod";
import type { Flag, HardStop, Locale } from "./common";
import type { FormTemplateContent, TemplateField } from "./intake-template";

export const preConsultSeveritySchema = z.enum([
  "urgent",
  "high",
  "medium",
  "low",
]);
export type PreConsultSeverity = z.infer<typeof preConsultSeveritySchema>;

export const preConsultRiskFlagSchema = z.object({
  rule: z.string(),
  kind: z.enum(["hard_stop", "flag"]),
  severity: preConsultSeveritySchema,
  escalate: z.enum(["none", "doctor", "urgent"]).optional(),
  detail: z.string().optional(),
  // TES-1030 — the answer keys behind the flag (flagSchema.fieldKeys), so the
  // doctor can jump from the flag to the question and answer that raised it.
  // Absent when the rule named none.
  fieldKeys: z.array(z.string()).optional(),
  // TES-1001 part 1 — the versioned risk code and its priority, when the
  // flag comes from the risk-code list.
  riskCode: z.string().optional(),
  riskPriority: z.enum(["high", "elevated", "normal"]).nullable().optional(),
});
export type PreConsultRiskFlag = z.infer<typeof preConsultRiskFlagSchema>;

// TES-973 — present when a doctor's correction (TES-863, `medical_profile_entries`)
// replaced what she declared. `value` is then the corrected answer, and this
// carries the original beside it: the declaration is never edited, and a
// reader must never be able to mistake a correction for her own answer.
export const preConsultCorrectionSchema = z.object({
  // Her declared answer as rendered, or null when she left it empty.
  originalValue: z.string().nullable(),
  correctedAt: z.string(),
  // The doctor's display name. Doctor-facing surfaces only; the AI readback
  // renders through `summaryItemText`, which leaves it out.
  correctedBy: z.string().nullable(),
});
export type PreConsultCorrection = z.infer<typeof preConsultCorrectionSchema>;

export const preConsultSummaryItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.string(),
  correction: preConsultCorrectionSchema.optional(),
});
export type PreConsultSummaryItem = z.infer<typeof preConsultSummaryItemSchema>;

export const preConsultSummarySectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  items: z.array(preConsultSummaryItemSchema),
});
export type PreConsultSummarySection = z.infer<
  typeof preConsultSummarySectionSchema
>;

export const preConsultSummarySchema = z.object({
  riskFlags: z.array(preConsultRiskFlagSchema),
  sections: z.array(preConsultSummarySectionSchema),
  answeredCount: z.number().int(),
  flagCount: z.number().int(),
  hardStopCount: z.number().int(),
});
export type PreConsultSummary = z.infer<typeof preConsultSummarySchema>;

// TES-973 — the doctor's corrections that apply to ONE submission, keyed by
// the form field they replace, with the corrected answer in the form's own
// answer shape (option ids, medication entries) so it renders exactly like
// hers. Built by the API from the current head of each correction chain.
export type PreConsultCorrections = Record<
  string,
  { value: unknown; correctedAt: string; correctedBy: string | null }
>;

const SEVERITY_ORDER: Record<PreConsultSeverity, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

// Dot-path read into the nested answers tree (mirrors the mobile renderer).
function getAnswer(answers: Record<string, unknown>, path: string): unknown {
  let cursor: unknown = answers;
  for (const segment of path.split(".")) {
    if (typeof cursor !== "object" || cursor === null) return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

function hasValue(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function optionLabel(
  field: TemplateField,
  value: unknown,
  locale: Locale,
): string {
  const opt = field.options?.find((o) => o.value === value);
  return opt ? opt.label[locale] : String(value);
}

export function formatMedication(entry: unknown): string {
  if (entry && typeof entry === "object") {
    const e = entry as Record<string, unknown>;
    const text = (k: string): string | undefined => {
      const v = e[k];
      return typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
    };
    const name = text("name") ?? "";
    // §4.5 split strength / frequency / reason into their own parts. The
    // doctor reads this summary before the meeting, so every part the patient
    // filled in has to reach it — rendering only the legacy field would have
    // silently dropped "hur ofta" and "anledning" from the clinical picture.
    //
    // `doseAndFrequency` is the pre-split shape and is still rendered for
    // submissions stored before the split.
    const parts = [
      text("strength"),
      text("frequency"),
      text("doseAndFrequency"),
    ].filter((p): p is string => p !== undefined);
    const reason = text("reason");
    if (reason) parts.push(`skäl: ${reason}`);
    return parts.length > 0 ? `${name} (${parts.join(", ")})` : name;
  }
  return String(entry);
}

function resolveValue(
  field: TemplateField,
  value: unknown,
  locale: Locale,
): string {
  switch (field.kind) {
    case "boolean":
      return value === true
        ? locale === "en"
          ? "Yes"
          : "Ja"
        : locale === "en"
          ? "No"
          : "Nej";
    // A consent that reached a submission was given — the schema admits no
    // other value — so the informative half of the row is the sentence in the
    // label, not a "Ja" that could never have been anything else. Naming the
    // act keeps the doctor's list readable as a consent record.
    case "consent":
      return locale === "en" ? "Consented" : "Samtycker";
    case "single_select":
      return optionLabel(field, value, locale);
    case "multi_select":
      return Array.isArray(value)
        ? value.map((v) => optionLabel(field, v, locale)).join(", ")
        : String(value);
    case "scale_1_5":
      return `${String(value)}/5`;
    case "numeric":
      return field.unit ? `${String(value)} ${field.unit}` : String(value);
    case "medications_list":
      return Array.isArray(value)
        ? value.map(formatMedication).filter(Boolean).join("; ")
        : String(value);
    // F-07: the answer is the upload ids — meaningless to read. The count is
    // the summary; the files themselves are listed by the case view.
    case "file_upload": {
      const n = Array.isArray(value) ? value.length : 0;
      if (locale === "en") return n === 1 ? "1 attachment" : `${n} attachments`;
      return n === 1 ? "1 bilaga" : `${n} bilagor`;
    }
    case "date":
    case "short_text":
    case "long_text":
    default:
      return String(value);
  }
}

// TES-1441 — intake v18 asks the register's per-symptom questions once per
// symptom she chose, each worded "… av detta …" with the symptom's name as
// its help line (checkin-symptom-fields.ts). Without the name, the doctor
// would read "Hur mycket besvär har du av detta just nu?: Måttligt" once per
// symptom and could not tell them apart. F-02 only: the check-ins' readers
// (the monthly review journal's sources) quote the question as she was
// asked it and name the symptom themselves.
const PER_SYMPTOM_QUESTIONS: ReadonlySet<string> = new Set([
  "SYMPTOM_SEVERITY",
  "SYMPTOM_TREND",
  "SYMPTOM_STARTED_AT",
  "SYMPTOM_INTAKE",
  "SYMPTOM_NOTE",
]);

function summaryLabel(
  template: FormTemplateContent,
  field: TemplateField,
  locale: Locale,
): string {
  const label = field.label[locale];
  if (
    template.key === "intake" &&
    field.help &&
    field.questionId &&
    PER_SYMPTOM_QUESTIONS.has(field.questionId)
  ) {
    return `${field.help[locale]} – ${label}`;
  }
  return label;
}

export function buildPreConsultSummary(input: {
  template: FormTemplateContent;
  answers: Record<string, unknown>;
  flags: Flag[];
  hardStops: HardStop[];
  locale: Locale;
  corrections?: PreConsultCorrections;
}): PreConsultSummary {
  const { template, answers, flags, hardStops, locale, corrections } = input;

  // Risk flags first: hard stops (urgent vs blocking) then flags by severity.
  const riskFlags: PreConsultRiskFlag[] = [
    ...hardStops.map(
      (h): PreConsultRiskFlag => ({
        rule: h.rule,
        kind: "hard_stop",
        severity: h.urgent ? "urgent" : "high",
        detail: h.detail,
        ...(h.fieldKeys?.length ? { fieldKeys: h.fieldKeys } : {}),
      }),
    ),
    ...flags.map(
      (f): PreConsultRiskFlag => ({
        rule: f.rule,
        kind: "flag",
        severity: f.severity,
        escalate: f.escalate,
        detail: f.detail,
        ...(f.fieldKeys?.length ? { fieldKeys: f.fieldKeys } : {}),
        ...(f.riskCode
          ? { riskCode: f.riskCode, riskPriority: f.riskPriority ?? null }
          : {}),
      }),
    ),
  ].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  let answeredCount = 0;
  const sections: PreConsultSummarySection[] = [];
  for (const section of template.sections) {
    const items: PreConsultSummaryItem[] = [];
    for (const field of section.fields) {
      const value = getAnswer(answers, field.key);
      const fix = corrections?.[field.key];
      if (fix) {
        const original = hasValue(value)
          ? resolveValue(field, value, locale)
          : null;
        const corrected = hasValue(fix.value)
          ? resolveValue(field, fix.value, locale)
          : null;
        // A correction that reads the same as her answer changes nothing
        // here (the doctor corrected another part of the same statement).
        if (corrected !== original) {
          answeredCount += 1;
          items.push({
            key: field.key,
            label: summaryLabel(template, field, locale),
            value: corrected ?? NO_ANSWER[locale],
            correction: {
              originalValue: original,
              correctedAt: fix.correctedAt,
              correctedBy: fix.correctedBy,
            },
          });
          continue;
        }
      }
      if (!hasValue(value)) continue;
      answeredCount += 1;
      items.push({
        key: field.key,
        label: summaryLabel(template, field, locale),
        value: resolveValue(field, value, locale),
      });
    }
    // Skip empty sections — keep the summary tight.
    if (items.length > 0) {
      sections.push({ id: section.id, title: section.title[locale], items });
    }
  }

  return {
    riskFlags,
    sections,
    answeredCount,
    flagCount: flags.length,
    hardStopCount: hardStops.length,
  };
}

const NO_ANSWER: Record<Locale, string> = { sv: "Ingen uppgift", en: "None" };

// The correction date as a Swedish calendar day — a correction at 00:30 is
// that day in Stockholm, not the previous one in UTC.
export function correctionDate(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    timeZone: "Europe/Stockholm",
  });
}

/**
 * TES-973 — a summary item as one line of text, for every reader that
 * renders the summary as text (AI readback, journal prefill, flag answers).
 * A corrected item says so and keeps her original beside it; the doctor's
 * name is deliberately left out, because this text reaches the model.
 */
export function summaryItemText(
  item: PreConsultSummaryItem,
  locale: Locale = "sv",
): string {
  const c = item.correction;
  if (!c) return item.value;
  const date = correctionDate(c.correctedAt);
  const original = c.originalValue ?? NO_ANSWER[locale];
  return locale === "en"
    ? `${item.value} (corrected by doctor ${date}; declared: ${original})`
    : `${item.value} (korrigerad av läkare ${date}; patienten angav: ${original})`;
}
